/**
 * useVideoQoe — wires VideoQoeController to the live player on pandatv-pc.
 *
 * One controller per play attempt (keyed by channel). It survives RT→LL
 * failover: mode is derived from the store, not the render-lagged `isRT` prop,
 * so element rebinds and the mode-switch event fire against the right element.
 *
 * Everything the controller needs that IS observable from outside is gathered
 * here — element events, store transitions, and a 20s gauge poll. The rest
 * (error classification, failover reason, retry counts) arrives via the signal
 * channel.
 */

import type { PlayInfo } from '@module/apis/client-side/live/type';
import useOldPlayerStore, { getIVSMediaPlayer } from '@module/stores/useOldPlayerStore';
import { type RefObject, useEffect, useRef } from 'react';
import { VideoQoeController } from './controller';
import { subscribeVideoQoeSignals } from './signals';
import type { RemoteVideoStatsLike, RemoteVideoStatsSource, VideoMode, VideoQoeGauges } from './types';
import { VIDEO_QOE_HEARTBEAT_MS } from './types';
interface UseVideoQoeOptions {
  playInfo: PlayInfo;
  videoElementRef: RefObject<HTMLVideoElement | null> | null;
  getRemoteVideoStream?: () => RemoteVideoStatsSource | null;
  disabled?: boolean;
}
const pickVideoStats = (stats: readonly unknown[] | undefined): RemoteVideoStatsLike | undefined => stats?.find(s => {
  const v = s as RemoteVideoStatsLike;
  return v.framesDecoded != null || v.packetsReceived != null;
}) as RemoteVideoStatsLike | undefined;
export function useVideoQoe({
  playInfo,
  videoElementRef,
  getRemoteVideoStream,
  disabled = false
}: UseVideoQoeOptions): void {
  const videoElementRefBox = useRef(videoElementRef);
  videoElementRefBox.current = videoElementRef;
  const getRemoteVideoStreamBox = useRef(getRemoteVideoStream);
  getRemoteVideoStreamBox.current = getRemoteVideoStream;
  useEffect(() => {
    if (disabled) return;
    const hasRT = !!playInfo.PlayList?.whip?.[0]?.token;
    const isRtNow = (): boolean => hasRT && useOldPlayerStore.getState().streamMode === 'RT';
    const currentMode = (): VideoMode => isRtNow() ? 'rt' : 'll';
    const activeElement = (): HTMLVideoElement | null => isRtNow() ? videoElementRefBox.current?.current ?? null : getIVSMediaPlayer(useOldPlayerStore.getState().ivsPlayer)?.getHTMLVideoElement() ?? null;
    const controller = new VideoQoeController({
      context: {
        channelId: playInfo.channel,
        bjId: playInfo.media.userId,
        hasRT,
        streamType: playInfo.media.type
      },
      initialMode: currentMode()
    });
    controller.noteVisibility(document.visibilityState === 'visible');
    controller.startView();

    // ─── element listeners (rebound on element/mode change) ───
    let boundEl: HTMLVideoElement | null = null;
    let rvfcHandle: number | null = null;
    const onPlaying = () => {
      if (boundEl && boundEl.videoWidth > 0) controller.noteFirstFrame('playing');
      controller.notePlaying();
    };
    const onWaiting = () => controller.noteWaiting();
    const onPause = () => controller.notePause();
    const unbind = () => {
      if (!boundEl) return;
      boundEl.removeEventListener('playing', onPlaying);
      boundEl.removeEventListener('waiting', onWaiting);
      boundEl.removeEventListener('pause', onPause);
      if (rvfcHandle != null && typeof boundEl.cancelVideoFrameCallback === 'function') {
        boundEl.cancelVideoFrameCallback(rvfcHandle);
      }
      rvfcHandle = null;
      boundEl = null;
    };
    const bind = (el: HTMLVideoElement | null) => {
      if (el === boundEl) return;
      unbind();
      boundEl = el;
      if (!el) return;
      el.addEventListener('playing', onPlaying);
      el.addEventListener('waiting', onWaiting);
      el.addEventListener('pause', onPause);
      if (typeof el.requestVideoFrameCallback === 'function') {
        rvfcHandle = el.requestVideoFrameCallback(() => controller.noteFirstFrame('rvfc'));
      }
    };
    bind(activeElement());

    // ─── signal channel ───
    const unsubscribeSignals = subscribeVideoQoeSignals(signal => controller.noteSignal(signal));

    // ─── store transitions ───
    const unsubscribeStore = useOldPlayerStore.subscribe((state, prev) => {
      if (state.streamMode !== prev.streamMode) {
        controller.noteModeChange(currentMode());
        bind(activeElement());
      }
      if (state.ivsPlayer !== prev.ivsPlayer) {
        if (prev.ivsPlayer && state.ivsPlayer) controller.noteLLInstanceChanged();
        const sessionId = getIVSMediaPlayer(state.ivsPlayer)?.getSessionId?.();
        if (sessionId) controller.setIvsSessionId(sessionId);
        bind(activeElement());
      }
      if (state.currentQuality !== prev.currentQuality && state.currentQuality) {
        const auto = isRtNow() ? state.isAutoQuality : !!getIVSMediaPlayer(state.ivsPlayer)?.isAutoQualityMode();
        controller.noteQuality(state.currentQuality.bitrate, state.currentQuality.height, auto ? 'auto' : 'manual');
      }
    });

    // ─── 20s heartbeat ───
    let flushing = false;
    const flush = async () => {
      if (flushing) return;
      flushing = true;
      try {
        const gauges: VideoQoeGauges = {};
        const el = activeElement();
        if (el && typeof el.getVideoPlaybackQuality === 'function') {
          const q = el.getVideoPlaybackQuality();
          gauges.droppedFrames = q.droppedVideoFrames;
          gauges.totalFrames = q.totalVideoFrames;
        }
        if (isRtNow()) {
          const stream = getRemoteVideoStreamBox.current?.();
          const stats = await stream?.requestQualityStats?.();
          gauges.rt = pickVideoStats(stats);
        } else {
          const ivs = getIVSMediaPlayer(useOldPlayerStore.getState().ivsPlayer);
          if (ivs) {
            gauges.bitrateBps = ivs.getQuality()?.bitrate;
            gauges.llLatencySec = ivs.getLiveLatency();
            gauges.llBufferSec = ivs.getBufferDuration();
          }
        }
        controller.recordHeartbeat(gauges);
      } finally {
        flushing = false;
      }
    };
    const intervalId = window.setInterval(() => void flush(), VIDEO_QOE_HEARTBEAT_MS);
    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      controller.noteVisibility(visible);
      if (!visible) void flush();
    };
    const onPageHide = () => controller.endView('pagehide');
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      unsubscribeSignals();
      unsubscribeStore();
      unbind();
      controller.dispose();
    };
  }, [disabled, playInfo.channel]);
}